import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  PhoneAndroid as MobileIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Interfaces
interface Notification {
  id: number;
  notification_type: string;
  always: boolean;
  web: boolean;
  mail: boolean;
  sms: boolean;
  calendar: number;
  attributes: {
    message: string;
    priority: string;
  };
  isRead?: boolean; // Optional for UI state
}

interface ApiResponse {
  responseData: Notification[];
  status: boolean;
  message: string;
}

interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  message: string;
  type: 'email' | 'sms' | 'push' | 'in-app';
  isActive: boolean;
  trigger: 'geofence' | 'maintenance' | 'alert' | 'system';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface FormState {
  name: string;
  subject: string;
  message: string;
  type: 'email' | 'sms' | 'push' | 'in-app';
  isActive: boolean;
  trigger: 'geofence' | 'maintenance' | 'alert' | 'system';
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const API_BASE_URL = 'http://43.204.149.250:2546/gpstracker/notificationhandler';

const NotificationManagement: React.FC = () => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // API state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Templates state
  const [templates, setTemplates] = useState<NotificationTemplate[]>([
    {
      id: '1',
      name: 'Maintenance Reminder',
      subject: 'Vehicle Maintenance Due',
      message: '{{vehicle_name}} ({{vehicle_number}}) is due for scheduled maintenance on {{date}}.',
      type: 'email',
      isActive: true,
      trigger: 'maintenance'
    },
    {
      id: '2',
      name: 'Geofence Alert',
      subject: 'Geofence Breach Alert',
      message: '{{vehicle_name}} ({{vehicle_number}}) has {{entered|left}} the {{geofence_name}} area at {{time}}.',
      type: 'push',
      isActive: true,
      trigger: 'geofence'
    },
    {
      id: '3',
      name: 'Low Fuel SMS',
      subject: 'Low Fuel Warning',
      message: 'Vehicle {{vehicle_name}} ({{vehicle_number}}) is running low on fuel ({{percentage}}% remaining) in {{location}}.',
      type: 'sms',
      isActive: false,
      trigger: 'alert'
    },
    {
      id: '4',
      name: 'Driver Assignment',
      subject: 'New Assignment',
      message: 'You have been assigned to {{vehicle_name}} ({{vehicle_number}}) for {{date}} {{route}} delivery.',
      type: 'in-app',
      isActive: true,
      trigger: 'system'
    },
    {
      id: '5',
      name: 'E-Way Bill Expiry',
      subject: 'E-Way Bill Expiry',
      message: 'E-Way Bill for {{vehicle_name}} ({{vehicle_number}}) will expire in {{hours}} hours. Bill Number: {{bill_number}}',
      type: 'email',
      isActive: true,
      trigger: 'alert'
    },
    {
      id: '6',
      name: 'Compliance Reminder',
      subject: 'GST Filing Reminder',
      message: 'Monthly GST filing due in {{days}} days. Please ensure all transport invoices are updated.',
      type: 'email',
      isActive: true,
      trigger: 'system'
    },
    {
      id: '7',
      name: 'Route Deviation',
      subject: 'Route Deviation Alert',
      message: '{{vehicle_name}} ({{vehicle_number}}) has deviated from planned route in {{location}}.',
      type: 'push',
      isActive: true,
      trigger: 'geofence'
    },
    {
      id: '8',
      name: 'FASTag Low Balance',
      subject: 'FASTag Low Balance',
      message: 'FASTag for {{vehicle_name}} ({{vehicle_number}}) has low balance (₹{{amount}}). Please recharge immediately.',
      type: 'sms',
      isActive: true,
      trigger: 'alert'
    }
  ]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');

  // Dialog state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<NotificationTemplate | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Template form state
  const [formData, setFormData] = useState<FormState>({
    name: '',
    subject: '',
    message: '',
    type: 'email',
    isActive: true,
    trigger: 'system'
  });

  // Fetch notifications from API
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<ApiResponse>(`${API_BASE_URL}/?limit=${rowsPerPage}&offset=${page * rowsPerPage}`);

      if (response.data.status) {
        // Add isRead property to each notification for UI state
        const notificationsWithReadState = response.data.responseData.map(notification => ({
          ...notification,
          isRead: false // Default all to unread
        }));

        setNotifications(notificationsWithReadState);
        // Set total count for pagination
        // Note: API might provide total count in future
        setTotal(response.data.responseData.length + page * rowsPerPage);
      } else {
        setError(response.data.message || 'Failed to fetch notifications');
        showSnackbar('Failed to fetch notifications', 'error');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load notifications on initial render and when pagination changes
  useEffect(() => {
    fetchNotifications();
  }, [page, rowsPerPage]);

  // Show snackbar message
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle refresh button click
  const handleRefresh = () => {
    fetchNotifications();
  };

  // Mark notification as read
  const handleMarkAsRead = (id: number) => {
    setNotifications(notifications.map(notification =>
      notification.id === id ? { ...notification, isRead: true } : notification
    ));

    // API call to mark as read could be added here
    showSnackbar('Notification marked as read', 'success');
  };

  // Delete notification
  const handleDeleteNotification = (id: number) => {
    // API call to delete notification could be added here
    setNotifications(notifications.filter(notification => notification.id !== id));
    showSnackbar('Notification deleted', 'success');
  };

  // Toggle template active status
  const handleToggleTemplate = (id: string) => {
    setTemplates(templates.map(template =>
      template.id === id ? { ...template, isActive: !template.isActive } : template
    ));
  };

  // Open template dialog
  const handleOpenTemplateDialog = (template?: NotificationTemplate) => {
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        message: template.message,
        type: template.type,
        isActive: template.isActive,
        trigger: template.trigger
      });
      setCurrentTemplate(template);
      setIsEditMode(true);
    } else {
      setFormData({
        name: '',
        subject: '',
        message: '',
        type: 'email',
        isActive: true,
        trigger: 'system'
      });
      setCurrentTemplate(null);
      setIsEditMode(false);
    }
    setTemplateDialogOpen(true);
  };

  // Close template dialog
  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle type select change
  const handleTypeChange = (e: SelectChangeEvent) => {
    setFormData({
      ...formData,
      type: e.target.value as 'email' | 'sms' | 'push' | 'in-app'
    });
  };

  // Handle trigger select change
  const handleTriggerChange = (e: SelectChangeEvent) => {
    setFormData({
      ...formData,
      trigger: e.target.value as 'geofence' | 'maintenance' | 'alert' | 'system'
    });
  };

  // Handle type filter change
  const handleTypeFilterChange = (e: SelectChangeEvent) => {
    setTypeFilter(e.target.value);
  };

  // Handle read filter change
  const handleReadFilterChange = (e: SelectChangeEvent) => {
    setReadFilter(e.target.value);
  };

  // Save template
  const handleSaveTemplate = () => {
    if (isEditMode && currentTemplate) {
      setTemplates(templates.map(template =>
        template.id === currentTemplate.id ? { ...template, ...formData } : template
      ));
      showSnackbar('Template updated successfully', 'success');
    } else {
      const newTemplate: NotificationTemplate = {
        id: `${templates.length + 1}`,
        ...formData as any
      };
      setTemplates([...templates, newTemplate]);
      showSnackbar('Template added successfully', 'success');
    }
    handleCloseTemplateDialog();
  };

  // Delete template
  const handleDeleteTemplate = (id: string) => {
    setTemplates(templates.filter(template => template.id !== id));
    showSnackbar('Template deleted', 'success');
  };

  // Get type icon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <EmailIcon />;
      case 'sms':
        return <SmsIcon />;
      case 'push':
        return <MobileIcon />;
      case 'in-app':
        return <NotificationsIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  // Get notification type color
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'reminder':
        return 'info';
      case 'alert':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      default:
        return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch =
      notification.notification_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.attributes.message.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      typeFilter === 'all' ||
      notification.notification_type === typeFilter;

    const matchesRead =
      readFilter === 'all' ||
      (readFilter === 'read' && notification.isRead) ||
      (readFilter === 'unread' && !notification.isRead);

    return matchesSearch && matchesType && matchesRead;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Notification Management
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="notification management tabs"
        >
          <Tab
            icon={<NotificationsIcon />}
            iconPosition="start"
            label="Notifications"
            id="notification-tab-0"
            aria-controls="notification-tabpanel-0"
          />
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label="Templates"
            id="notification-tab-1"
            aria-controls="notification-tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Notifications Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Search notifications"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '200px' }}
          />

          <FormControl size="small" sx={{ minWidth: '150px' }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={handleTypeFilterChange}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="reminder">Reminder</MenuItem>
              <MenuItem value="alert">Alert</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Information</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: '150px' }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={readFilter}
              label="Status"
              onChange={handleReadFilterChange}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="read">Read</MenuItem>
              <MenuItem value="unread">Unread</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={handleRefresh}
            title="Refresh"
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="notifications table">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Channels</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={40} sx={{ my: 3 }} />
                  </TableCell>
                </TableRow>
              ) : filteredNotifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No notifications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotifications.map((notification) => (
                  <TableRow
                    key={notification.id}
                    hover
                    sx={{
                      backgroundColor: notification.isRead ? 'inherit' : 'rgba(25, 118, 210, 0.04)'
                    }}
                  >
                    <TableCell>
                      {notification.isRead ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <NotificationsActiveIcon color="primary" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notification.notification_type}
                        size="small"
                        color={getNotificationColor(notification.notification_type) as any}
                      />
                    </TableCell>
                    <TableCell>{notification.attributes.message}</TableCell>
                    <TableCell>
                      <Chip
                        label={notification.attributes.priority}
                        size="small"
                        color={getPriorityColor(notification.attributes.priority) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {notification.web && <Chip icon={<NotificationsIcon />} label="Web" size="small" />}
                        {notification.mail && <Chip icon={<EmailIcon />} label="Email" size="small" />}
                        {notification.sms && <Chip icon={<SmsIcon />} label="SMS" size="small" />}
                        {notification.calendar === 1 && <Chip label="Calendar" size="small" />}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {!notification.isRead && (
                        <IconButton
                          size="small"
                          onClick={() => handleMarkAsRead(notification.id)}
                          title="Mark as read"
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteNotification(notification.id)}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TabPanel>

      {/* Templates Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenTemplateDialog()}
            sx={{
              bgcolor: "var(--primary-color)",
              "&:hover": {
                bgcolor: "var(--primary-light)",
              },
            }}
          >
            Add Template
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="templates table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTypeIcon(template.type)}
                      <Typography variant="body2">
                        {template.type.toUpperCase()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{template.subject}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: '300px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {template.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.trigger.toUpperCase()}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={template.isActive}
                      onChange={() => handleToggleTemplate(template.id)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenTemplateDialog(template)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTemplate(template.id)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={handleCloseTemplateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditMode ? 'Edit Notification Template' : 'Add Notification Template'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Template Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />

            <TextField
              fullWidth
              label="Subject/Title"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />

            <TextField
              fullWidth
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              multiline
              rows={4}
              required
              helperText="Use {{variable_name}} for dynamic content"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  label="Type"
                  onChange={handleTypeChange}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="push">Push Notification</MenuItem>
                  <MenuItem value="in-app">In-App Notification</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Trigger</InputLabel>
                <Select
                  name="trigger"
                  value={formData.trigger}
                  label="Trigger"
                  onChange={handleTriggerChange}
                >
                  <MenuItem value="geofence">Geofence Event</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="alert">System Alert</MenuItem>
                  <MenuItem value="system">General System</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Status:</Typography>
              <FormControl>
                <Switch
                  name="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({
                    ...formData,
                    isActive: e.target.checked
                  })}
                />
                <Typography variant="body2" color="text.secondary">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </Typography>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateDialog}>Cancel</Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={!formData.name || !formData.subject || !formData.message}
            sx={{
              bgcolor: "var(--primary-color)",
              "&:hover": {
                bgcolor: "var(--primary-light)",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationManagement; 